import React, { useContext, useEffect, useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { MenuContext } from '../../context/Menu';
import { MenuItemType } from './Type';
import MenuRoutes from './MenuList';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OrganizationContainer from './OrgInfo';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { hasAdminAccess, userLogin } from '@Services/hasAdminAccess';
import { CheckCondition, isRouteAllowed } from '../../utils/allowedActions';
import UserMenuRoutes from './UserMenuList';

// --------------------
// Styled Components with Glassmorphism
// --------------------

const MenuContainer = styled(Grid)<{ isMenuOpen?: boolean }>(({ theme, isMenuOpen }) => ({
  height: isMenuOpen ? 'calc(var(--layout-height) - 164px)' : 'calc(var(--layout-height) - 120px)',
  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.divider, 0.05),
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    borderRadius: '10px',
    transition: 'background-color 0.2s',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.4),
  },
}));

const MenuContentWrapper = styled('div')(() => ({
  padding: '16px',
  paddingTop: '8px',
  width: '100%',
}));

const ListItem = styled('div')<{ isMenuOpen?: boolean; active?: boolean }>(({ theme, isMenuOpen, active }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: isMenuOpen ? '10px 16px' : '10px',
  borderRadius: '8px',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  margin: '2px 0',

  background: active
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
        theme.palette.secondary.main || theme.palette.primary.main,
        0.02,
      )} 100%)`
    : 'transparent',
  border: active ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}` : '1px solid transparent',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.04),
    transform: 'translateX(4px)',
    '& svg': {
      color: theme.palette.primary.main,
    },
  },

  // Active indicator pill
  ...(active && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '20%',
      bottom: '20%',
      width: '4px',
      borderRadius: '0 4px 4px 0',
      background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${
        theme.palette.secondary.main || theme.palette.primary.dark
      })`,
      boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  }),
}));

const MenuItemWrapper = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  margin: '2px 0 !important',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.2s ease',

  '&:before': {
    display: 'none',
  },

  '&.Mui-expanded': {
    margin: '2px 0 !important',
    background: alpha(theme.palette.primary.main, 0.02),
  },
}));

const MenuItemLabel = styled('span')(({ theme }) => ({
  marginLeft: '16px',
  fontWeight: 500,
  fontSize: '14px',
  color: theme.palette.text.primary,
  flex: 1,
  letterSpacing: '0.2px',
}));

const NestedMenuItem = styled('li')<{ active?: boolean }>(({ theme, active }) => {
  const isActive = active;
  return {
    width: '100%',
    height: '38px',
    padding: '8px 16px 8px 48px',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    margin: '2px 0',
    position: 'relative',
    fontSize: '0.85rem',
    color: theme.palette.text.secondary || '#475569',

    background: isActive
      ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
          theme.palette.primary.main,
          0.02,
        )} 100%)`
      : 'transparent',

    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.04),
      transform: 'translateX(4px)',
      color: theme.palette.primary.main,
    },

    // Active state
    ...(isActive && {
      color: theme.palette.primary.main,
      fontWeight: 600,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 24,
        top: '30%',
        bottom: '30%',
        width: '3px',
        borderRadius: '3px',
        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${
          theme.palette.secondary.main || theme.palette.primary.dark
        })`,
        boxShadow: `0 0 6px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    }),

    '&[data-active="true"]': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
        theme.palette.primary.main,
        0.02,
      )} 100%)`,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 24,
        top: '30%',
        bottom: '30%',
        width: '3px',
        borderRadius: '3px',
        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${
          theme.palette.secondary.main || theme.palette.primary.dark
        })`,
        boxShadow: `0 0 6px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    },
  };
});

const CompactIconContainer = styled('div')<{
  active?: boolean;
}>(({ theme, active }) => ({
  position: 'relative',
  width: '44px',
  height: '44px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  background: active
    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${
        theme.palette.secondary.main || theme.palette.primary.dark
      })`
    : 'transparent',
  border: `1px solid ${active ? 'transparent' : alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: active ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',

  color: active ? '#ffffff' : theme.palette.text.secondary || '#475569',

  '& svg': {
    fontSize: '22px',
    zIndex: 2,
  },

  '&:hover': {
    transform: 'translateY(-2px)',
    background: active
      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${
          theme.palette.secondary.main || theme.palette.primary.dark
        })`
      : alpha(theme.palette.primary.main, 0.05),
    borderColor: active ? 'transparent' : alpha(theme.palette.primary.main, 0.15),
    color: active ? '#ffffff' : theme.palette.primary.main,
    boxShadow: active ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}` : `0 4px 12px rgba(0, 0, 0, 0.02)`,
  },
}));

const ToggleButtonContainer = styled(IconButton)<{ active?: boolean }>(({ theme, active }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  background: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: `0 2px 8px rgba(0, 0, 0, 0.02)`,
  color: theme.palette.text.secondary || '#475569',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.primary.main,
    transform: 'scale(1.05)',
    borderColor: alpha(theme.palette.primary.main, 0.15),
  },

  ...(active && {
    background: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  }),
}));

const PopoverMenuList = styled(List)(() => ({
  width: '220px',
  padding: '8px',
}));

const PopoverMenuItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '6px',
  margin: '2px 0',
  padding: '8px 12px',
  transition: 'all 0.15s ease',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.primary.main,
  },

  '&.Mui-selected': {
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
      theme.palette.primary.main,
      0.02,
    )} 100%)`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,

    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.08),
    },
  },
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    background: theme.palette.background.paper,
    borderRadius: '10px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: `0 10px 30px rgba(0, 0, 0, 0.08)`,
    overflow: 'hidden',
  },
}));

// --------------------
// Menu Item Component
// --------------------

function MenuItem(props: MenuItemType) {
  const { isMenuOpen, Icon, value, nestedPaths } = props;
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const loginUser = useSelector((state: RootStateType) => state.loginUser);
  const isPrepaid = loginUser?.profile?._accountId?.isPrepaid;
  const isAdmin = !userLogin();

  const hideWallet = !(!isAdmin && isPrepaid);
  const hideCredit = !(!isAdmin && !isPrepaid);

  const isActive = nestedPaths?.some(
    path => location.pathname === path.pathURL || location.pathname.startsWith(path.pathURL + '/'),
  );

  const filteredNestedPaths = nestedPaths?.filter(pathObj => {
    if (
      (hideWallet && ['/transactions/wallet'].includes(pathObj.pathURL)) ||
      (hideCredit && ['/transactions/credit'].includes(pathObj.pathURL)) ||
      (!isAdmin && ['/transactions/credits-and-wallets', '/transactions/list'].includes(pathObj.pathURL))
    ) {
      return false;
    }

    if (isAdmin && ['/my-rates'].includes(pathObj.pathURL)) {
      return false;
    }

    return true;
  });

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMenuOpen && filteredNestedPaths?.length) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleNestedItemClick = (pathURL: string) => {
    navigate(pathURL);
    handlePopoverClose();
  };

  // -------- Compact Menu with Popover --------
  if (!isMenuOpen) {
    return (
      <>
        <Tooltip
          title={value}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '13px',
                padding: '8px 12px',
                borderRadius: '10px',
                background: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.15)}`,
                animation: 'slideIn 180ms ease-out',
                color: theme.palette.primary.main,
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: alpha(theme.palette.background.paper, 0.95),
              },
            },
          }}
        >
          <div
            style={{
              margin: '12px 0',
              display: 'flex',
              justifyContent: 'center',
            }}
            onClick={handlePopoverOpen}
          >
            <CompactIconContainer active={isActive}>
              <Icon />
            </CompactIconContainer>
          </div>
        </Tooltip>

        <StyledPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <PopoverMenuList>
            {filteredNestedPaths?.map((pathObj, index) => {
              const isNestedActive =
                location.pathname === pathObj.pathURL || location.pathname.startsWith(pathObj.pathURL + '/');

              return (
                <PopoverMenuItem
                  key={`${pathObj.pathURL}-${index}`}
                  selected={isNestedActive}
                  onClick={() => handleNestedItemClick(pathObj.pathURL)}
                >
                  <ListItemText
                    primary={pathObj.label}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: isNestedActive ? 600 : 400,
                      color: isNestedActive ? theme.palette.primary.main : 'inherit',
                    }}
                  />
                </PopoverMenuItem>
              );
            })}
          </PopoverMenuList>
        </StyledPopover>
      </>
    );
  }

  // -------- Expanded Menu --------
  return (
    <MenuItemWrapper>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        sx={{
          padding: 0,
          margin: 0,
          minHeight: '40px',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginRight: '12px',
          },
        }}
      >
        <ListItem
          isMenuOpen={isMenuOpen}
          active={isActive}
          style={{
            flex: 1,
          }}
        >
          <Icon
            style={{
              width: '22px',
              height: '22px',
              color: isActive ? theme.palette.primary.main : 'inherit',
            }}
          />

          <MenuItemLabel
            style={{
              color: isActive ? theme.palette.primary.main : 'inherit',
              fontWeight: isActive ? 600 : 500,
            }}
          >
            {value}
          </MenuItemLabel>
        </ListItem>
      </AccordionSummary>

      <AccordionDetails sx={{ padding: '8px 0' }}>
        <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
          {filteredNestedPaths?.map((pathObj, index) => {
            const isNestedActive =
              location.pathname === pathObj.pathURL || location.pathname.startsWith(pathObj.pathURL + '/');

            return (
              <Link
                key={`${pathObj.pathURL}-${index}`}
                to={pathObj.pathURL}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <NestedMenuItem data-active={isNestedActive}>
                  <KeyboardArrowRightIcon
                    sx={{
                      fontSize: 16,
                      mr: 1,
                      opacity: 0.7,
                      color: isNestedActive ? theme.palette.primary.main : 'inherit',
                    }}
                  />
                  {pathObj.label}
                </NestedMenuItem>
              </Link>
            );
          })}
        </ul>
      </AccordionDetails>
    </MenuItemWrapper>
  );
}

// --------------------
// Main Menu Component
// --------------------

function Menu() {
  const { isMenuOpen, menuWidth, setIsMenuOpen } = useContext(MenuContext);

  const { PagesData, accessGroupById, SideNavbarPagesIds, SideNavbarActionsIds } = useSelector(
    (state: RootStateType) => state.accessGroup,
  );

  const { profile } = useSelector((state: RootStateType) => state.loginUser);

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);

  const { name = '' } = organizationData || {};

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isUserLogin = userLogin();
  const isAdmin = hasAdminAccess();

  const filteredPagesData = PagesData.filter(page => SideNavbarPagesIds.includes(page._id));

  const handleToggleMenu = () => {
    if (setIsMenuOpen) {
      setIsMenuOpen(prev => !prev);
    }
  };

  useEffect(() => {
    const check = CheckCondition(pathname);

    if (profile?.accessGroup === 'pu_all') {
      let matchFound = false;

      for (const menuRoute of UserMenuRoutes) {
        for (const nestedPath of menuRoute.nestedPaths) {
          if (pathname.toLowerCase().startsWith(nestedPath.pathURL.toLowerCase())) {
            matchFound = true;
            break;
          }
        }
        if (matchFound) break;
      }

      if (!matchFound && pathname !== '/') {
        navigate('/');
      }
    } else {
      if (check) {
        const actions = accessGroupById?.actions || [];

        if (actions.length > 0) {
          const res = isRouteAllowed(pathname, actions);
          if (!res) navigate(-1);
        }
      }
    }
  }, [accessGroupById?.pagesId, PagesData]);

  return (
    <>
      <MenuContainer container isMenuOpen={isMenuOpen}>
        <MenuContentWrapper>
          {!isUserLogin ? (
            <>
              {isAdmin ? (
                <>
                  {MenuRoutes.filter(menu => {
                    if (name === 'Cargo Solutions Network' && menu.label === 'Squad') {
                      return false;
                    }
                    return true;
                  }).map(menu => (
                    <MenuItem
                      key={menu.label}
                      isMenuOpen={isMenuOpen}
                      menuWidth={menuWidth || 308}
                      value={menu.label}
                      Icon={menu.Icon}
                      nestedPaths={menu.nestedPaths}
                    />
                  ))}
                </>
              ) : (
                <>
                  {MenuRoutes.map(menu => {
                    const filteredNestedPaths = menu?.nestedPaths?.filter(nestedPath => {
                      // always convert to array for uniform handling
                      const actions = Array.isArray(nestedPath?.matchActionName)
                        ? nestedPath.matchActionName
                        : [nestedPath?.matchActionName];

                      if (menu.label === 'Invoice') {
                        return filteredPagesData?.some(page =>
                          actions.some(action => page?.name?.toLowerCase().startsWith(action?.toLowerCase())),
                        );
                      } else {
                        return (
                          filteredPagesData?.some(page => actions.some(action => page?.name === action)) ||
                          SideNavbarActionsIds.some(item =>
                            actions.some(action => item.details?.name?.toLowerCase().includes(action?.toLowerCase())),
                          )
                        );
                      }
                    });

                    if (filteredNestedPaths?.length) {
                      return (
                        <MenuItem
                          key={menu.label}
                          isMenuOpen={isMenuOpen}
                          menuWidth={menuWidth || 308}
                          value={menu.label}
                          Icon={menu.Icon}
                          nestedPaths={filteredNestedPaths}
                        />
                      );
                    }

                    return null;
                  })}
                </>
              )}
            </>
          ) : (
            <>
              {UserMenuRoutes.map(menu => {
                const filteredNestedPaths = menu.nestedPaths.filter(
                  nestedPath => !(name === 'Cargo Solutions Network' && nestedPath.pathURL === '/my-rates'),
                );

                return (
                  <MenuItem
                    key={menu.label}
                    isMenuOpen={isMenuOpen}
                    menuWidth={menuWidth || 308}
                    value={menu.label}
                    Icon={menu.Icon}
                    nestedPaths={filteredNestedPaths}
                  />
                );
              })}
            </>
          )}

          <ToggleButtonContainer onClick={handleToggleMenu} active={!isMenuOpen} sx={{ alignSelf: 'center', mt: 4 }}>
            {isMenuOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </ToggleButtonContainer>
        </MenuContentWrapper>

        {isMenuOpen && <OrganizationContainer />}
      </MenuContainer>
    </>
  );
}

export default Menu;
